/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CustomerCodesFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Class of Services module.
----------------------------------------------------------------*/


import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tag, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import { Label } from '../../components/ui/label';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { FormBox } from '../../components/ui/FormBox';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useContextStore } from '../../store/context';
import { deleteCustomerCodeById, navigateToCustomerCodeDuplicate } from './customerCodesActions';
import { useCustomerCodeData } from './hooks/useCustomerCodeData';
import { useCustomerCodeForm } from './hooks/useCustomerCodeForm';
import { fetchQuickListItems } from './api';
import type { QuickListItem } from './types';

export default function CustomerCodesFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const isDuplicateMode = Boolean(duplicateId);

  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError } = useNotifications();

  const {
    selectedTenant,
    selectedDomain
  } = useContextStore();

  const tenantSlug = selectedTenant?.slug ?? 'main';

  const { formData: initialData, isLoading } = useCustomerCodeData({
    id,
    duplicateId,
    tenantSlug,
    isDuplicateMode
  });

  const {
    formData,
    setFormData,
    originalData,
    errors,
    isSaving,
    hasChanges,
    handleChange,
    handleSave
  } = useCustomerCodeForm({
    id,
    duplicateId,
    initialData,
    isEditMode,
    isDuplicateMode
  });

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();
    // If no changes were detected, return current formData to allow navigation
    if (!savedData) {
      return formData;
    }
    return savedData;
  }, [handleSave, formData]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/class-of-service/customer-codes',
    newPath: '/pbx/class-of-service/customer-codes/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/class-of-service/customer-codes/${savedData?.uuid || savedData?.id || id}`,
    getFormData: () => formData,
    storageKey: 'customerCodes:saveAndNewData',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<QuickListItem[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!id || id === 'new') {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteCustomerCodeById(id, tenantSlug);

      showSuccess(t('customerCodes.messages.deleteSuccess'));
      navigate('/pbx/class-of-service/customer-codes');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customerCodes.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [id, navigate, showError, showSuccess, t, tenantSlug]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToCustomerCodeDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/class-of-service/customer-codes/new');
  }, [navigate]);

  const loadQuickList = useCallback(async () => {
    if (!selectedDomain?.id) {
      showError(t('customerCodes.messages.domainMissing'));
      return;
    }

    setIsQuickListLoading(true);
    try {
      const items = await fetchQuickListItems(selectedDomain.id, tenantSlug);
      setQuickListItems(items);
    } catch {
      showError(t('customerCodes.messages.loadError'));
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
    }
  }, [selectedDomain?.id, showError, t, tenantSlug]);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.code.toLowerCase().includes(query) ||
      (item.description ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  const pageTitle = !isEditMode || isDuplicateMode
    ? t('customerCodes.form.titleNew')
    : `${t('customerCodes.form.titleEdit')} - ${formData.code || ''}`;

  // Show loading state only when editing and data hasn't loaded yet
  if (isLoading && !initialData && isEditMode && !isDuplicateMode) {
    return (
      <div className="p-6 pb-24">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  // For new entries or when data is available, show the form
  // useCustomerCodeForm will handle default values if initialData is null
  return (
    <div className="p-6 pb-24"><ModuleFormHeader
        icon={<Tag className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('customerCodes.actions.list', 'List')}
        onBack={() => navigate('/pbx/class-of-service/customer-codes')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('customerCodes.actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              loadQuickList();
            },
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('customerCodes.actions.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('customerCodes.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <form className="mt-8 space-y-6">
        <FormBox title={t('customerCodes.title')} isLoading={isLoading}>
          {(!selectedDomain || errors.domainId) && (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {t('customerCodes.messages.domainMissing')}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
            <Label htmlFor="customer-code-value" className="text-sm font-medium text-muted-foreground">
              {t('customerCodes.fields.code.label')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <div>
              <Input
                id="customer-code-value"
                value={formData.code}
                placeholder={t('customerCodes.fields.code.placeholder')}
                onChange={(event) => handleChange('code', event.target.value)}
                className={errors.code ? 'border-destructive' : undefined}
              />
              {errors.code && (
                <p className="mt-1 text-xs text-destructive">{errors.code}</p>
              )}
            </div>

            <Label htmlFor="customer-code-description" className="text-sm font-medium text-muted-foreground">
              {t('customerCodes.fields.description.label')}
            </Label>
            <div>
              <Input
                id="customer-code-description"
                value={formData.description}
                placeholder={t('customerCodes.fields.description.placeholder')}
                onChange={(event) => handleChange('description', event.target.value)}
              />
            </div>

            <Label htmlFor="customer-code-active" className="text-sm font-medium text-muted-foreground">
              {t('customerCodes.fields.active')}
            </Label>
            <div>
              <Toggle
                value={formData.active}
                onChange={(value) => handleChange('active', value)}
              />
            </div>
          </div>
        </FormBox>

        <FixedActionBar
          onSaveWithAction={handleSaveWithAction}
          onCancel={() => {
            if (id === 'new') {
              setFormData({ code: '', description: '', active: true });
            } else if (originalData) {
              setFormData({...originalData});
            }
          }}
          onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
          isSaving={isSaving}
          isEditMode={isEditMode}
          useSaveDropdown={true}
          saveText={t('common.save')}
          cancelText={t('common.cancel')}
          deleteText={t('common.delete')}
        />
      </form>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={t('customerCodes.messages.deleteConfirm')}
        message={t('customerCodes.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
        }}
        title={t('customerCodes.quickList.title', 'Customer Codes')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/class-of-service/customer-codes/${(item as any).uuid || item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/class-of-service/customer-codes/${(item as any).uuid || item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.code}
            </div>
            {item.description && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.description}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('customerCodes.quickList.searchPlaceholder', 'Search by code or description')}
        emptyMessage={t('customerCodes.quickList.empty', 'No customer codes found')}
        headerIcon={<Tag className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
